import { AdminLayout } from "@/components/Admin/AdminLayout"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Link } from "@inertiajs/react"
import { ArrowLeft, User, Package, CreditCard } from "lucide-react"

interface OrderItem {
  id: number
  product_name: string | null
  product_slug: string | null
  price_cents: number
  quantity: number
}

interface OrderDetail {
  id: number
  user_email: string
  user_name: string
  total_cents: number
  status: string
  asaas_checkout_id?: string | null
  asaas_payment_id?: string | null
  created_at: string
  paid_at?: string | null
  user: {
    id: number
    email: string
    name: string
  }
  items: OrderItem[]
}

interface Props {
  order: OrderDetail
}

const formatCents = (cents: number) => (cents / 100).toFixed(2).replace(".", ",")

const formatDateTime = (value?: string | null) => {
  if (!value) return "-"
  const date = new Date(value)
  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "long",
    timeStyle: "short",
  }).format(date)
}

const getStatusBadgeClass = (status: string) => {
  switch (status) {
    case "paid":
      return "bg-green-100 text-green-800"
    case "pending":
      return "bg-yellow-100 text-yellow-800"
    case "failed":
      return "bg-red-100 text-red-800"
    default:
      return "bg-gray-100 text-gray-800"
  }
}

const getStatusLabel = (status: string) => {
  switch (status) {
    case "paid":
      return "Pago"
    case "pending":
      return "Pendente"
    case "failed":
      return "Falhou"
    default:
      return status
  }
}

export default function OrdersShow({ order }: Props) {
  return (
    <AdminLayout title={`Pedido #${order.id}`}>
      {/* Back button */}
      <div className="mb-4">
        <Button variant="ghost" size="sm" asChild>
          <Link href="/admin/orders">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar para pedidos
          </Link>
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Order Summary */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Package className="h-5 w-5" />
                Pedido #{order.id}
              </h2>
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeClass(
                  order.status
                )}`}
              >
                {getStatusLabel(order.status)}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Criado em</p>
                <p className="font-medium">{formatDateTime(order.created_at)}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Pago em</p>
                <p className="font-medium">{formatDateTime(order.paid_at)}</p>
              </div>
            </div>
          </Card>

          {/* Order Items */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Itens do Pedido</h3>
            {order.items.length === 0 ? (
              <p className="text-muted-foreground text-sm">Nenhum item encontrado</p>
            ) : (
              <div className="space-y-3">
                {order.items.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between py-3 border-b last:border-0"
                  >
                    <div>
                      <p className="font-medium">{item.product_name || "Produto removido"}</p>
                      {item.product_slug && (
                        <p className="text-xs text-muted-foreground">SKU: {item.product_slug}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="font-medium">R$ {formatCents(item.price_cents)}</p>
                      <p className="text-xs text-muted-foreground">Qtd: {item.quantity}</p>
                    </div>
                  </div>
                ))}
                <div className="flex items-center justify-between pt-3 border-t font-semibold">
                  <span>Total</span>
                  <span className="text-lg">R$ {formatCents(order.total_cents)}</span>
                </div>
              </div>
            )}
          </Card>

          {/* ASAAS Info */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Informacoes ASAAS
            </h3>
            <div className="space-y-3 text-sm">
              <div>
                <p className="text-muted-foreground">Session ID</p>
                <p className="font-mono text-xs break-all">{order.asaas_checkout_id || "-"}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Payment ID</p>
                <p className="font-mono text-xs break-all">{order.asaas_payment_id || "-"}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Sidebar - User Info */}
        <div className="space-y-6">
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <User className="h-5 w-5" />
              Cliente
            </h3>
            <div className="space-y-3 text-sm">
              <div>
                <p className="text-muted-foreground">Nome</p>
                <p className="font-medium">{order.user.name || "-"}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Email</p>
                <p className="font-medium">{order.user.email}</p>
              </div>
              <div className="pt-2">
                <Button variant="outline" size="sm" className="w-full" asChild>
                  <Link href={`/admin/users/${order.user.id}`}>
                    Ver perfil do usuário
                  </Link>
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </AdminLayout>
  )
}
